import { createMemo, Index, type Accessor, type JSX } from 'solid-js';
export { measureWidth, wrapText } from './fitText';
import { wrapText } from './fitText';
import { evaluateExpression } from 'Shared/common/posterTemplate';
import { parseBorder } from '../styleAttrs';
import { resolveEffectiveStyle, type ThemeRecord } from '../../project/themeVariables';
import type { Style } from '../../project/posterSchema';
import type { RuntimeObject, RuntimeText, RuntimeContainer, DataRecord } from '../../types';

// ── Style helpers ─────────────────────────────────────────────────────────────

const UNITLESS = new Set(['lineHeight', 'fontWeight', 'opacity', 'zIndex', 'flexGrow', 'flexShrink'])

function toCssValue(prop: string, val: string | number | boolean): string {
	if (typeof val === 'number' && !UNITLESS.has(prop)) return val + 'px'
	return String(val)
}

// Derive text-anchor and dominant-baseline from CSS flex alignment on parent element.
const JC_ANCHOR: Record<string, [string, number]> = {
	center:      ['middle', 0.5],
	'flex-end':  ['end',    1],
}

export function deriveTextLayout(elementStyle: Style | undefined, elW: number | undefined, elH: number | undefined) {
	const jc = elementStyle?.justifyContent as string | undefined ?? 'flex-start'
	const ai = elementStyle?.alignItems    as string | undefined ?? 'flex-start'
	const [anchor, xFrac] = JC_ANCHOR[jc] ?? ['start', 0]
	const isCentered = ai === 'center'
	return {
		anchor,
		x:        xFrac * (elW ?? 0),
		baseline: isCentered ? 'central' : 'auto',
		y:        isCentered ? (elH ?? 0) / 2 : 0,
	}
}

// The downward shift that places a text block of contentHeight within a box of boxHeight, per the
// vertical alignment (object.alignment): 0 / ½ / full of the leftover slack for top / center / bottom.
// Shared by TextNode (which applies it to the rendered text) and TemplateTextImpl (which subtracts it
// back out when measuring the intrinsic baseline dy). Returns 0 when the box hugs its content (no slack).
export function verticalAlignOffset(align: string | undefined, boxHeight: number, contentHeight: number): number {
	const slack = boxHeight - contentHeight
	if (slack <= 0) return 0
	if (align === 'center') return slack / 2
	if (align === 'bottom') return slack
	return 0
}

// ── Template interpolation ────────────────────────────────────────────────────

// missingAsEmpty: when a placeholder resolves to null/undefined, render it as '' (user
// mode) instead of leaving the literal {expr} in place (design mode, so the designer can
// still see the unresolved field).
export function interpolateTemplate(template: string | null | undefined, record: DataRecord, missingAsEmpty = false): string {
	const src = template ?? ''
	let result = ''
	let i = 0
	while (i < src.length) {
		if (src[i] === '\\' && i + 1 < src.length) {
			const next = src[i + 1]
			if (next === '\\' || next === '{' || next === '}') {
				result += next
				i += 2
				continue
			}
		}
		if (src[i] === '{') {
			const end = src.indexOf('}', i + 1)
			if (end !== -1) {
				const expr = src.slice(i + 1, end)
				const val = evaluateExpression(expr, record)
				result += val != null ? String(val) : (missingAsEmpty ? '' : `{${expr}}`)
				i = end + 1
				continue
			}
		}
		result += src[i]
		i++
	}
	return result
}

// True when a text object renders to nothing — an empty or whitespace-only string — for the
// current record. Covers both plain text (its literal content) and a bound field (its template
// resolved against the record, with missing values dropped). In a user-facing context such a text
// box is omitted from the poster entirely: a flex container reclaims its slot and an absolute box
// simply disappears, with no diagonal-stripe placeholder. Design mode keeps every text box visible
// regardless. Non-text objects are never empty.
export function textRendersEmpty(object: RuntimeObject, record: DataRecord): boolean {
	if (object.type !== 'text') return false
	const text = object as RuntimeText
	const raw  = text.content ?? ''
	const rendered = text.style?.isField ? interpolateTemplate(raw, record, true) : raw
	return rendered.trim() === ''
}

// ── Template crop detection ─────────────────────────────────────────────────────
//
// Whether a template (field) text object's resolved content overflows its designed line count — i.e.
// design mode crops it (see TemplateTextImpl / TEXT_OBJECT_DESIGN.txt). Mirrors that crop as a free
// function — resolve the template, wrap it at the field width and designed font, and compare the line
// count to minLines (the template's own newlines + 1) — so the canvas-level "cropped" indicator can
// test every object against the *current* record. A free function rather than the registered impls
// because those capture the record at mount, so they can't answer for a later-selected record.

// True when this object is a template text whose resolved content needs more than minLines to display.
export function isTemplateContentCropped(object: RuntimeObject, record: DataRecord, theme: ThemeRecord | undefined): boolean {
	if (object.type !== 'text') return false
	const text = object as RuntimeText
	if (!text.style?.isField) return false
	const effStyle = resolveEffectiveStyle(text, theme)
	const minLines = (text.content ?? '').split('\n').length
	const font     = (effStyle?.fontSize ?? 24) as number
	const width    = text.size?.width ?? 200
	const resolved = interpolateTemplate(text.content ?? '', record, false)
	return wrapText(resolved, width, { ...effStyle, fontSize: font }).length > minLines
}

// True when any object in the tree (recursing into containers) is a cropped template text.
export function anyTemplateContentCropped(objects: RuntimeObject[], record: DataRecord, theme: ThemeRecord | undefined): boolean {
	return objects.some(obj => {
		if (isTemplateContentCropped(obj, record, theme)) return true
		const kids = (obj as RuntimeContainer).objects
		return Array.isArray(kids) ? anyTemplateContentCropped(kids, record, theme) : false
	})
}

// ── TextNode ──────────────────────────────────────────────────────────────────
//
// The single <text> element a TextObject renders. Every visual input is reactive: the caller
// passes the effective style, element box, resolved content and (optional) font override / wrapped
// lines, and TextNode derives the SVG layout from them with memos — so a
// style/size/content change updates the affected attributes in place rather than rebuilding the
// whole subtree (and the textRefs entry is registered once, not on every change).

export interface TextNodeProps {
	objectId:          string
	textRefs?:         Record<string, SVGTextElement>
	elW?:              number
	elH?:              number
	style?:            Style
	textContent:       Accessor<string>
	lineHeight:        Accessor<number>
	textAlign:         Accessor<string>
	// Vertical placement of the text block within the box (object.alignment). See the dual-use note on
	// verticalOffset below; absent (plain text, whose box hugs its content) leaves the text at the top.
	verticalAlign?:    Accessor<'top' | 'center' | 'bottom'>
	yOffset?:          number
	overrideFontSize?: () => number | null
	// Width-driven wrapped lines; undefined means fall back to the content's explicit newlines.
	wrappedLines?:     () => string[] | undefined
}

export function TextNode(P: TextNodeProps): JSX.Element {
	const layout   = createMemo(() => deriveTextLayout(P.style, P.elW, P.elH))
	const baseline = createMemo(() => (P.style?.dominantBaseline as string | undefined) ?? layout().baseline)

	// The font override (fields render at the designed/frozen font) wins; otherwise the designed numeric
	// fontSize, else null (a non-numeric/absent size leaves font-size unset so the SVG default applies).
	const fontSize = createMemo<number | null>(() => {
		// The field strategies return a number; plain text returns null → fall through to the designed
		// size. (Previously this branched on the prop's presence; now every strategy supplies the
		// accessor, so it branches on the returned value instead.)
		const override = P.overrideFontSize?.()
		if (override != null) return override
		const fs = P.style?.fontSize
		return typeof fs === 'number' ? fs : null
	})

	const textStyle = createMemo(() => buildTextStyle(P.style, fontSize()))

	const anchor = createMemo(() => {
		switch (P.textAlign()) {
			case 'left':  return 'start'
			case 'right': return 'end'
			default:      return 'middle'
		}
	})
	const x = createMemo(() => {
		switch (P.textAlign()) {
			case 'left':  return 0
			case 'right': return P.elW ?? layout().x
			default:      return P.elW != null ? P.elW / 2 : layout().x
		}
	})

	// Width-wrapped lines when supplied, otherwise the content's explicit newlines.
	const lines = createMemo(() => P.wrappedLines?.() ?? P.textContent().split('\n'))

	// object.alignment (verticalAlign) has a dual role. It anchors which edge of a template field's box
	// stays fixed when the box auto-resizes (see TemplateTextImpl.anchorTop / templateResize), and — here
	// — it places the text block vertically within a box that is taller than the text. verticalOffset is
	// the leftover slack (box height − rendered content height) times 0 / ½ / 1 for top / center / bottom.
	// A box that hugs its content exactly (plain text, and a field once its box is fitted to the content)
	// has no slack, so this is a no-op there and the text stays at the top as before.
	// Ceil to match wrappedHeight()'s rounding, so a box that hugs its content nets exactly zero slack.
	const contentHeight  = createMemo(() => Math.ceil(lines().length * (fontSize() ?? 16) * P.lineHeight()))
	const verticalOffset = createMemo(() => {
		// Only the default (non-flex) layout leaves the text at the top for us to re-place; the flex
		// alignItems:center path (layout.baseline === 'central') already centres it, so don't double up.
		if (layout().baseline !== 'auto') return 0
		return verticalAlignOffset(P.verticalAlign?.(), P.elH ?? 0, contentHeight())
	})
	const ny = createMemo(() => layout().y + (P.yOffset ?? 0) + verticalOffset())

	return (
		<text
			ref={el => { if (P.textRefs) P.textRefs[P.objectId] = el }}
			x={x()} y={ny()}
			text-anchor={anchor() as 'inherit' | 'start' | 'middle' | 'end' | undefined}
			dominant-baseline={baseline() as 'auto' | 'inherit' | 'middle' | 'central' | 'text-bottom' | 'alphabetic' | 'ideographic' | 'mathematical' | 'hanging' | 'text-top' | undefined}
			style={textStyle()}
		>
			<Index each={lines()}>
				{(line, i) => (
					<tspan x={x()} dy={i === 0 ? 0 : (fontSize() ?? 16) * P.lineHeight()}>{line()}</tspan>
				)}
			</Index>
		</text>
	)
}

// Build the SVG text presentation style from the element style and the effective font size,
// dropping unset properties so they inherit/default.
function buildTextStyle(s: Style | undefined, fontSize: number | null): Record<string, string | undefined> {
	const stroke = s?.['-webkit-text-stroke'] ? parseBorder(s['-webkit-text-stroke'] as string) : undefined
	const st: Record<string, string | undefined> = {
		fill:             (s?.color as string | undefined) ?? '#000',
		'font-family':    s?.fontFamily as string | undefined,
		'font-size':      fontSize != null ? toCssValue('fontSize', fontSize)
		                  : (s?.fontSize != null ? toCssValue('fontSize', s.fontSize as number) : undefined),
		'font-weight':    s?.fontWeight != null ? String(s.fontWeight) : undefined,
		'font-style':     s?.fontStyle as string | undefined,
		'text-decoration': s?.textDecoration as string | undefined,
		'letter-spacing': s?.letterSpacing != null ? toCssValue('letterSpacing', s.letterSpacing as number) : undefined,
		'text-transform': s?.textTransform as string | undefined,
		'paint-order':    s?.paintOrder as string | undefined,
		stroke:           s?.['-webkit-text-stroke'] ? stroke?.color ?? 'none' : undefined,
		'stroke-width':   s?.['-webkit-text-stroke'] ? (stroke?.width ?? 0) + 'px' : undefined,
	}
	for (const k of Object.keys(st)) if (st[k] == null) delete st[k]
	return st
}
